import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { FormattedMessage, useIntl } from 'react-intl';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useState } from 'react';
import { placeAutoComplete } from '../service/PlaceService';
import { PlaceAutoCompleteResult } from '../model/Places';
import { debounce } from 'lodash';
import { useChangeLocation, useCurrentLocation } from '../hook/useFilterAction';
import NearMeIcon from '@mui/icons-material/NearMe';

type LocationFilterProps = {
  setSearchLocation: (searchLocation: boolean) => void;
};

export const LocationFilter = ({ setSearchLocation }: LocationFilterProps) => {
  const intl = useIntl();
  const changeLocation = useChangeLocation();
  const [typeaheadList, setTypeAheadList] = useState<PlaceAutoCompleteResult[]>(
    [],
  );
  const [selectingLocationItem, setSelectingLocationItem] = useState(false);

  const debouncedSearchLocationFunc = debounce(
    useCallback(async (term: string) => {
      if (term.length > 0) {
        const autoCompleteResult = await placeAutoComplete(term);
        setTypeAheadList(autoCompleteResult);
      } else {
        setTypeAheadList([]);
      }
    }, []),
    300,
  );

  const onSelectCurrentLocation = async () => {
    console.debug('Select current location');
    setSelectingLocationItem(true);
    await selectCurrentLocation();
  };

  const onSuccessGetPosition = () => {
    setSelectingLocationItem(false);
    setSearchLocation(false);
  };

  const onErrorGetPosition = () => {
    setSelectingLocationItem(false);
  };

  const selectCurrentLocation = useCurrentLocation(
    onSuccessGetPosition,
    onErrorGetPosition,
  );

  const selectLocationItem = useCallback(
    async (typeahead: PlaceAutoCompleteResult) => {
      setSelectingLocationItem(true);
      await changeLocation(typeahead);
      setSelectingLocationItem(false);
      setSearchLocation(false);
    },
    [setSelectingLocationItem, changeLocation, setSearchLocation],
  );

  const itemList = typeaheadList.map((typeahead) => (
    <ListItem key={typeahead.placeId} divider disablePadding>
      <ListItemButton
        disabled={selectingLocationItem}
        onClick={() => selectLocationItem(typeahead)}
        sx={{ px: 0, py: 5 }}
      >
        <ListItemIcon sx={{ minWidth: 32 }} />
        <ListItemText primary={typeahead.description} />
      </ListItemButton>
    </ListItem>
  ));
  return (
    <Box>
      <Box sx={{ p: 5, display: 'flex', alignItems: 'center' }}>
        <IconButton
          sx={{ width: 40, height: 40, p: 0 }}
          onClick={() => setSearchLocation(false)}
        >
          <NavigateBeforeIcon sx={{ fontSize: '2rem' }} />
        </IconButton>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          <FormattedMessage id='community.filter.location.title' />
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ py: 5 }}>
        <TextField
          onChange={(event) =>
            debouncedSearchLocationFunc(event.currentTarget.value)
          }
          id='outlined-basic'
          variant='outlined'
          autoFocus
          fullWidth
          placeholder={intl.formatMessage({
            id: 'community.filter.location.search-placeholder',
          })}
          sx={{
            px: 5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 7.5,
              background: '#fff',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: '#b1b5bd' }} />
              </InputAdornment>
            ),
          }}
        />
        <List sx={{ pt: 8, px: 5 }}>
          <ListItem divider disablePadding>
            <ListItemButton
              sx={{ px: 0, py: 5 }}
              disabled={selectingLocationItem}
              onClick={onSelectCurrentLocation}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <NearMeIcon sx={{ color: 'text.primary' }} />
              </ListItemIcon>
              <ListItemText>
                <FormattedMessage id='community.filter.location.current-location' />
              </ListItemText>
            </ListItemButton>
          </ListItem>
          {itemList}
        </List>
      </Box>
    </Box>
  );
};
